<template>
  <div v-if="hasResponse && roadmapHost.isByValueRoadmap" id="chart-container" class="by-lift-roadmap">
    <div id="cp-by-lift-roadmap-e-chart" class="chart-row">
      <profile-chart-header
        :title="titleSuffix ? title + ' | ' + titleSuffix : title"
        :display="hasResponse"
        :content-list="contentList"
        :content-name="title"
        :display-download-comments="true"
        :input-display-number-of-responses="displayNumberOfResponses"
        :input-show-tooltip="showTooltip"
        @set-display-number-of-responses="setDisplayNumberOfResponses"
        @set-show-tooltip="setShowTooltip"
      />
      <p v-if="focusError" class="text-danger" role="alert">{{ focusError }}</p>
      <lift-map
        v-if="liftTableData.rows.length"
        ref="eChart"
        :input-lift-map-data="liftTableData"
        :input-show-tooltip="showTooltip"
        :input-display-number-of-responses="displayNumberOfResponses"
        :input-respondent-count="respondentCount"
        :input-display-initiatives="inputDisplayInitiatives"
        e-chart-id="roadmap-lift-map-chart"
        e-chart-mini-id="roadmap-lift-map-chart-mini"
        :input-client-chart="roadmapHost.clientChart"
        :is-saved-card="!!roadmapHost.clientChart.clientChartId"
        :client-id="roadmapHost.clientId"
        :input-download-file-name="downloadFileName"
        @display-initiatives="displayInitiatives"
        @toggle-focus="toggleFocus"
      />
      <p v-else class="text-muted p-3" role="status">No lift data available.</p>
    </div>

    <div id="lift-roadmap-data-container" :class="roadmapHost.isInsiderUser ? 'chart-data hide-table-data' : 'chart-data'">
      <div v-if="roadmapHost.isInsiderUser" class="overlay">
        <img class="overlay-inner" src="../../../assets/img/overlap.png" alt="" style="width: 50%" />
      </div>
      <button type="button" class="lift-table-toggle" :aria-expanded="showLiftTable" aria-controls="lift-roadmap-table-panel" @click="showLiftTable = !showLiftTable">
        <span>{{ showLiftTable ? 'Hide Lift Table' : 'Show Lift Table' }}</span>
        <i :class="showLiftTable ? 'fa fa-chevron-up' : 'fa fa-chevron-down'" aria-hidden="true"></i>
      </button>
      <div v-show="showLiftTable" id="lift-roadmap-table-panel">
      <lift-table-actions
        v-if="!roadmapHost.isInsiderUser"
        :table-data="sortedLiftTableData"
        :metric-columns="metricColumns"
        :filename="titleSuffix ? 'Lift Map Table - ' + titleSuffix : 'Lift Map Table'"
        snapshot-container-id="lift-roadmap-table"
      />
      <div class="table-responsive">
        <table id="lift-roadmap-table" class="lift-table">
          <caption class="sr-only">Lift Map Table</caption>
          <thead>
            <tr>
              <th v-for="column in tableColumns" :key="column.field" scope="col" :class="column.headerClass" :aria-sort="liftSortField === column.field ? liftSortDirection : 'none'">
                <button type="button" class="lift-sort-button" @click="sortLiftTable(column.field)">
                  <template v-if="column.field === 'evidence'">Odds<br />in Top 5</template>
                  <template v-else>{{ column.label }}</template>
                  <i :class="liftSortField === column.field ? (liftSortDirection === 'ascending' ? 'fa fa-sort-up' : 'fa fa-sort-down') : 'fa fa-sort'" aria-hidden="true"></i>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in sortedLiftTableData.rows" :key="`${row.needType}-${row.sequenceNumber}-${index}`">
              <td class="lift-rank">{{ row.rank }}</td>
              <td class="lift-label">{{ row.label }}</td>
              <th scope="row">{{ row.need }}</th>
              <td v-for="column in metricColumns" :key="column.field" class="lift-metric">{{ formatPercent(row[column.field], column.digits, column.percent !== false) }}</td>
            </tr>
            <tr v-if="!liftTableData.rows.length"><td :colspan="3 + metricColumns.length" class="text-center text-muted">No lift data available.</td></tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="2"></td>
              <th scope="row">Premium</th>
              <td class="lift-metric lift-total">{{ formatPercent(liftTableData.summary.premiumWtp, 2) }}</td>
              <th scope="row" class="lift-metric">Averages</th>
              <td v-for="field in averageFields" :key="field" class="lift-metric">{{ formatPercent(liftTableData.summary.averages[field], 1) }}</td>
              <td :colspan="metricColumns.length - 2 - averageFields.length"></td>
            </tr>
            <tr class="lift-table-stake-total">
              <td colspan="2"></td><th scope="row">Table Stake</th>
              <td class="lift-metric lift-total">{{ formatPercent(liftTableData.summary.tableStakeWtp, 2) }}</td><td :colspan="metricColumns.length - 1"></td>
            </tr>
            <tr>
              <td colspan="2"></td><th scope="row" class="lift-growth-total">Growth Total</th>
              <td class="lift-metric lift-total lift-growth-total">{{ formatPercent(liftTableData.summary.growthTotalWtp, 2) }}</td><td :colspan="metricColumns.length - 1"></td>
            </tr>
          </tfoot>
        </table>
      </div>
      </div>
    </div>

    <div id="roadmap-initiatives" class="chart-row">
    
      <!-- reordering saving to parent -->
      <road-map-initiatives
        ref="roadMapInitiatives"
        :title-suffix="titleSuffix"
        :input-lift-map-data="liftTableData"
        :input-show-tooltip="showTooltip"
        :input-display-number-of-responses="displayNumberOfResponses"
        :input-respondent-count="respondentCount"
        :input-display-initiatives="inputDisplayInitiatives"
        :is-saved-card="!!roadmapHost.clientChart.clientChartId"
        :client-id="roadmapHost.clientId"
        :input-download-file-name="downloadFileName"
        :effort-sets="roadmapEffortSets"
        :saved-focus="savedFocus"
        :saved-initiative-sequence="savedInitiativeSequence"
        :content-list="contentList"
        @display-initiatives="displayInitiatives"
        @toggle-focus="toggleFocus"
        @reorder-initiatives="saveReorderedInitiatives"
      />
      
    </div>
  </div>
</template>

<script>
import LiftMap from "../../echarts/LiftMap.vue";
import ProfileChartHeader from "../../../components/echarts/ProfileChartHeader.vue";
import LiftTableActions from "../../../components/chart/LiftTableActions.vue";
import ChartCompanyMixins from "../../../mixins/ChartCompanyMixins.vue";
import ValueGroupMapService from "../../../services/chart/value.group.map.service";
import AdminService from "../../../services/admin.service";
import LiftSummaryService from "../../../services/chart/lift.summary.service";
import LiftRowOrderService from "../../../services/chart/lift.row.order.service";
import RoadMapInitiatives from "../../../components/chart/RoadMapInitiatives.vue";

export default {
  name: "ByLiftRoadmap",
  components: { LiftMap, ProfileChartHeader, LiftTableActions, RoadMapInitiatives },
  props: {
    chartData: { type: Array, default: () => [] },
    contentList: { type: Array, default: () => [] },
    hostApp: { type: Object, default: null },
    inputDisplayInitiatives: { type: Boolean, default: true },
  },
  data() {
    return {
      title: "Lift Map",
      displayType: "value-roadmap",
      showLiftTable: true,
      liftSortField: "",
      liftSortDirection: "descending",
      showTooltip: true,
      displayNumberOfResponses: true,
      focusAreaEfforts: [],
      otherAreaEfforts: [],
      defaultEffortSets: [],
      valueRoadMapId: 0,
      savedFocus: {},
      pendingFocus: {},
      focusError: "",
      focusVersion: 0,
      effortLoadId: 0,
      effortsLoaded: false,
      recommendationsData: {},
      averageFields: ["inDemand", "reposition", "tableStake", "peripheral"],
      metricColumns: [
        { field: "capturableWtp", label: "Capturable WTP %", digits: 2 },
        { field: "capturablePremium", label: "Capturable Premium %", digits: 1 },
        { field: "inDemand", label: "In Demand %", digits: 1 },
        { field: "reposition", label: "Reposition %", digits: 1 },
        { field: "tableStake", label: "Table Stake %", digits: 1 },
        { field: "peripheral", label: "Peripheral %", digits: 1 },
        { field: "evidence", label: "Odds in Top 5", digits: 1 },
        { field: "valueGap", label: "Value Gap", digits: 2, percent: false }
      ]
    };
  },
  computed: {
    roadmapHost() {
      return this.hostApp || this.$parent;
    },
    surveyCode: function() {
      return this.roadmapHost.$data.survey.surveyCode;
    },       
    roadmapEffortSets() {
      const defaultSets = this.roadmapHost && this.roadmapHost.defaultEffortSets;
      const firstSet = Array.isArray(defaultSets) ? defaultSets[0] : null;
      const focusAreaEfforts = firstSet && Array.isArray(firstSet.focusAreaEfforts) ? firstSet.focusAreaEfforts : [];
      const otherAreaEfforts = firstSet && Array.isArray(firstSet.otherAreaEfforts) ? firstSet.otherAreaEfforts : [];
      return [...focusAreaEfforts, ...otherAreaEfforts];
    },
    hasResponse() {
      return !!this.chartData[0];
    },
    otherData() {
      return this.hasResponse ? this.chartData[0].otherData || {} : {};
    },
    liftTableData() {
      // /performance returns LiftMap wrappers with the need details in the nested Lift.
      const liftList = Array.isArray(this.otherData.liftList) ? this.otherData.liftList : [];
      const summary = this.otherData.liftSummary || {};
      const rows = liftList.map((liftMap, index) => ({
        ...liftMap.lift,
        lift: liftMap.lift,
        rank: liftMap.rank,
        label: liftMap.label || "",
        need: liftMap.lift ? liftMap.lift.needName : "",
        capturableWtp: liftMap.capturableWtp,
        // The server supplies premium as a fraction; display metrics use 0–100.
        capturablePremium: typeof liftMap.capturablePremium === "number" ? liftMap.capturablePremium * 100 : null,
        inDemand: liftMap.inDemandPercent,
        reposition: liftMap.repositionPercent,
        tableStake: liftMap.tableStackPercent,
        peripheral: liftMap.peripheralPercent,
        evidence: typeof liftMap.pTop5 === "number" ? liftMap.pTop5 : null,
        valueGap: liftMap.valueGap,
        marketValue: liftMap.marketValue ?? (liftMap.lift && liftMap.lift.marketValue),
        initiative: "",
        focus: !!this.savedFocus[this.focusKey(liftMap.lift)],
        focusDisabled: !this.valueRoadMapId || !!this.pendingFocus[this.focusKey(liftMap.lift)],
        // twoMarket is a fraction; the marker axis is expressed in percent.
        isTwoMarkets: typeof liftMap.twoMarket === "number" && Number.isFinite(liftMap.twoMarket) ? liftMap.twoMarket * 100 : null
      }));
      // const initiatives = [
      //   ...this.defaultEffortSets,
      //   ...this.otherAreaEfforts];
      return {
        rows: LiftRowOrderService.sortRows(rows),
        summary: LiftSummaryService.buildSummary(rows, summary)
      };
    },
    tableColumns() {
      return [
        { field: "rank", label: "RANK", headerClass: "lift-rank" },
        { field: "label", label: "Label", headerClass: "lift-label" },
        { field: "need", label: "Need" },
        ...this.metricColumns.map(column => ({ ...column, headerClass: "lift-metric" }))
      ];
    },
    sortedLiftTableData() {
      if (!this.liftSortField) return this.liftTableData;
      const direction = this.liftSortDirection === "ascending" ? 1 : -1;
      const rows = [...this.liftTableData.rows].sort((a, b) => {
        const left = a[this.liftSortField];
        const right = b[this.liftSortField];
        if (this.liftSortField === "label" || this.liftSortField === "need") {
          const leftText = typeof left === "string" ? left.trim() : "";
          const rightText = typeof right === "string" ? right.trim() : "";
          if (!leftText) return rightText ? 1 : 0;
          if (!rightText) return -1;
          return leftText.localeCompare(rightText, undefined, { sensitivity: "base", numeric: true }) * direction;
        }
        // Missing values stay at the bottom in either direction.
        if (!Number.isFinite(left)) return Number.isFinite(right) ? 1 : 0;
        if (!Number.isFinite(right)) return -1;
        return (left - right) * direction;
      });
      return { ...this.liftTableData, rows };
    },
    titleSuffix() {
      if (this.otherData.titleSuffix) return this.otherData.titleSuffix;
      const criteria = this.roadmapHost.$refs.chartCriteria;
      return criteria ? criteria.getPeriodName(this.roadmapHost.clientChart.clientTimePeriodId || criteria.period) : "";
    },
    respondentCount() {
      return typeof this.otherData.respondentCount === "number" ? this.otherData.respondentCount : null;
    },
    downloadFileName() {
      return this.titleSuffix ? "Lift Map - " + this.titleSuffix : "Lift Map";
    },
    savedInitiativeSequence() {
      const savedCardCriteria = this.roadmapHost.selectedSavedCard && this.roadmapHost.selectedSavedCard.criteria
        ? this.roadmapHost.selectedSavedCard.criteria
        : "";
      const clientChartCriteria = this.roadmapHost.clientChart && this.roadmapHost.clientChart.criteria
        ? this.roadmapHost.clientChart.criteria
        : "";
      const criteria = savedCardCriteria || clientChartCriteria;
      const params = new URLSearchParams(criteria);
      const savedSequence = params.get("roadmap_initiative_sequence");
      if (!savedSequence) return [];
      try {
        const parsedSequence = JSON.parse(savedSequence);
        return Array.isArray(parsedSequence) ? parsedSequence : [];
      } catch (e) {
        return [];
      }
    }
  },
  methods: {
    sortLiftTable(field) {
      this.liftSortDirection = this.liftSortField === field && this.liftSortDirection === "descending" ? "ascending" : "descending";
      this.liftSortField = field;
    },
    focusKey(lift) {
      return lift ? `${String(lift.needType).toLowerCase()}:${lift.sequenceNumber}` : "";
    },
    async toggleFocus(row) {
      const key = this.focusKey(row.lift);
      if (!this.valueRoadMapId || !row.lift || this.pendingFocus[key]) return;
      const version = this.focusVersion;
      const selected = typeof row.savedFocus === "boolean" ? row.savedFocus : !this.savedFocus[key];
      const previousSavedFocus = { ...this.savedFocus };
      const surveyCode = this.roadmapHost.surveyCode || (this.roadmapHost.survey && this.roadmapHost.survey.surveyCode);
      this.savedFocus = { ...this.savedFocus, [key]: selected };
      this.pendingFocus = { ...this.pendingFocus, [key]: true };
      this.focusError = "";
      try {
        const method = selected ? "saveValueRoadMapFocusArea" : "removeValueRoadMapFocusArea";
        // const lift = {
        //   ...row.lift,
        //   valueRoadMapId: this.valueRoadMapId
        // };
        const liftObj = {
          needName: row.lift.needName,
          needType: row.lift.needType,
          sequenceNumber: row.lift.sequenceNumber,
          differentiator: row.lift.differentiator,
          tableStakes: row.lift.tableStakes,
          harvest: row.lift.harvest,
          none: row.lift.none,
          reposition: row.lift.reposition,
          inDemand: row.lift.inDemand,
          repositionColor: row.lift.repositionColor,
          inDemandColor: row.lift.inDemandColor,
          repositionCredibilityLevel: row.lift.repositionCredibilityLevel,
          inDemandCredibilityLevel: row.lift.inDemandCredibilityLevel
        };
        
        await ValueGroupMapService[method](surveyCode, this.valueRoadMapId, liftObj);
        if (version === this.focusVersion) {
          this.savedFocus = { ...this.savedFocus, [key]: selected };
          this.$emit("focus-changed", this.valueRoadMapId);
        }
      } catch (error) {
        if (version === this.focusVersion) {
          this.savedFocus = previousSavedFocus;
          this.focusError = error.message || "Unable to update Focus. Please try again.";
        }
      } finally {
        if (version === this.focusVersion) this.pendingFocus = { ...this.pendingFocus, [key]: false };
      }
    },
    setRecommendationsData(data, isPositiveCorrelation) {
      ChartCompanyMixins.methods.setRecommendationsData.call(this, data, isPositiveCorrelation);
      this.$emit("set-recommendations-data", this.recommendationsData);
    },
    isDefaultTypeEmpty(focusAreaEfforts, otherAreaEfforts) {
      return !(Array.isArray(focusAreaEfforts) && focusAreaEfforts.length) &&
        !(Array.isArray(otherAreaEfforts) && otherAreaEfforts.length);
    },
    switchToDefault(defaultTypeName) {
      const defaultSet = this.defaultEffortSets.find(set => set.name === defaultTypeName);
      if (defaultSet && !this.isDefaultTypeEmpty(defaultSet.focusAreaEfforts, defaultSet.otherAreaEfforts)) {
        this.setEffortSet(defaultSet.focusAreaEfforts, defaultSet.otherAreaEfforts);
      }
    },
    setEffortSet(focusAreaEfforts, otherAreaEfforts) {
      this.focusAreaEfforts = JSON.parse(JSON.stringify(Array.isArray(focusAreaEfforts) ? focusAreaEfforts : []));
      this.otherAreaEfforts = JSON.parse(JSON.stringify(Array.isArray(otherAreaEfforts) ? otherAreaEfforts : []));
    },
    getChartCriteriaParams(chart, chartCriteria) {
      return this.roadmapHost.getChartCriteriaParams(chart, chartCriteria);
    },
    setShowTooltip(value) {
      this.showTooltip = value;
      this.saveDisplayOptions();
    },
    setDisplayNumberOfResponses(value) {
      this.displayNumberOfResponses = value;
      this.saveDisplayOptions();
    },
    saveDisplayOptions() {
      const criteria = this.roadmapHost.$refs.chartCriteria;
      criteria.eChartCriteria.showTooltip = this.showTooltip;
      criteria.eChartCriteria.displayNumberOfResponses = this.displayNumberOfResponses;
      this.buildClientChart();
      const params = new URLSearchParams(this.roadmapHost.clientChart.criteria);
      params.set("showTooltip", this.showTooltip);
      params.set("displayNumberOfResponses", this.displayNumberOfResponses);
      this.roadmapHost.clientChart.criteria = params.toString();
    },
    formatPercent(value, digits, percent = true) {
      return typeof value === "number" && Number.isFinite(value) ? value.toFixed(digits) + (percent ? "%" : "") : "—";
    },
    async processChartData() {
      if (!this.hasResponse || !this.roadmapHost.isByValueRoadmap) return;
      const options = this.roadmapHost.$refs.chartCriteria.eChartCriteria || {};
      if (typeof options.showTooltip === "boolean") this.showTooltip = options.showTooltip;
      if (typeof options.displayNumberOfResponses === "boolean") this.displayNumberOfResponses = options.displayNumberOfResponses;
      const table = this.chartData[0];
      this.roadmapHost.setChartResults(table.companyList || {});
      this.roadmapHost.criteriaResultsList = (table.criteriaList || []).filter(criteria => criteria.name !== "Competitor");
      this.valueRoadMapId = this.otherData.valueRoadMapId || 0;
      this.focusVersion++;
      this.pendingFocus = {};
      this.focusError = "";
      this.savedFocus = Object.fromEntries((this.otherData.focusAreas || []).map(area => [
        this.focusKey({ needType: area.needType, sequenceNumber: area.needNumber }), true
      ]));
      this.$emit("set-value-roadmap-id", this.valueRoadMapId);
      this.roadmapHost.setDisplayView(true);
      this.buildClientChart();
      const needsData = this.chartData[1];
      const additionalNeedsData = this.chartData[2];
      if (needsData && Array.isArray(needsData.cols)) {
        const recommendationsData = JSON.parse(JSON.stringify({
          ...needsData,
          rows: [
            ...(Array.isArray(needsData.rows) ? needsData.rows : []),
            ...(additionalNeedsData && Array.isArray(additionalNeedsData.rows) ? additionalNeedsData.rows : [])
          ]
        }));
        const isPositiveCorrelation = recommendationsData.otherData && recommendationsData.otherData.isPositiveCorrelation;
        this.setRecommendationsData(recommendationsData, typeof isPositiveCorrelation === "boolean" ? isPositiveCorrelation : true);
      } else {
        this.recommendationsData = {};
      }
      this.$emit("set-recommendations-data", this.recommendationsData);
      //return this.loadInitiativeData();
    },
    buildClientChart() {
      ChartCompanyMixins.methods.rebuildClientChart.call({ $parent: this.roadmapHost }, this.roadmapHost.clientId);
    },
    generateNextClientChartLink() {
      this.roadmapHost.generateNextClientChartLink();
    },
    handleAddInitiative(initiative, needName) {
      const element = [
        ...this.focusAreaEfforts,
        ...this.otherAreaEfforts
      ].find(e => e.name === needName);

      if (element) {
        element.initiative = initiative.initiative;
        element.description = initiative.description;
      }
    },
    displayInitiatives(row) {
      this.$emit("display-initiatives", row);
    },
    getRoadmapInitiativeSequenceForSave() {
      const initiativesTable = this.$refs.roadMapInitiatives;
      if (initiativesTable && typeof initiativesTable.getRoadmapInitiativeSequenceForSave == "function") {
        return initiativesTable.getRoadmapInitiativeSequenceForSave();
      }
      return [];
    },
    saveReorderedInitiatives(initiatives) {
      // reordering saving to parent
      if (!this.roadmapHost.defaultEffortSets ||
          !this.roadmapHost.defaultEffortSets[0] ||
          !Array.isArray(this.roadmapHost.defaultEffortSets[0].otherAreaEfforts)) {
        return;
      }
      const sequenceByNeed = (initiatives || []).reduce((sequenceMap, initiative) => {
        const key = this.focusKey({
          needType: initiative.needType,
          sequenceNumber: initiative.needNumber
        });
        sequenceMap[key] = initiative.sequenceNumber;
        return sequenceMap;
      }, {});
      const updateEffortSequences = effortList => {
        // reordering saving to parent
        return (effortList || []).map(effort => {
          const key = this.focusKey({
            needType: effort.needType,
            sequenceNumber: effort.needNumber
          });
          if (typeof sequenceByNeed[key] != "undefined") {
            return {
              ...effort,
              sequenceNumber: sequenceByNeed[key]
            };
          }
          return effort;
        });
      };
      this.roadmapHost.defaultEffortSets[0].otherAreaEfforts =
        updateEffortSequences(this.roadmapHost.defaultEffortSets[0].otherAreaEfforts);
    }
  },
  mounted() {
    this.processChartData();
  },
  beforeUnmount() {
    this.focusVersion++;
    this.effortLoadId++;
  },
  watch: {
    chartData() {
      this.processChartData();
    },
    surveyCode: function() {
      const roadmapHost = this.roadmapHost;
      const criteria = roadmapHost && roadmapHost.$refs && roadmapHost.$refs.chartCriteria;
      if (criteria && typeof criteria.resetRoadmapV2Criteria === "function") {
        criteria.resetRoadmapV2Criteria();
      } else if (roadmapHost && typeof roadmapHost.resetRoadmapV2 === "function") {
        roadmapHost.resetRoadmapV2();
      }
    }
  }
};
</script>

<style scoped>
.by-lift-roadmap > .chart-row {
  display: block;
}
.by-lift-roadmap .lift-map-container {
  min-width: 0;
  width: 100%;
}
.lift-table-toggle {
  display: flex;
  align-items: center;
  gap: 48px;
  width: 100%;
  padding: 12px 0;
  border: 0;
  border-top: 1px solid #2855ad;
  background: transparent;
  color: #2855ad;
  font-size: 12px;
  font-weight: bold;
  text-align: left;
  cursor: pointer;
}
.lift-table-toggle:focus-visible {
  outline: 2px solid #2855ad;
  outline-offset: 3px;
}
.lift-table {
  width: 100%;
  min-width: 1050px;
  border-collapse: collapse;
  font-family: "Franklin Gothic Demi Cond", "Arial Narrow", Arial, sans-serif;
  font-size: 12px;
  line-height: 1.35;
  color: #111;
}
.lift-table th,
.lift-table td {
  padding: 3px 7px;
  vertical-align: middle;
}
.lift-table thead th {
  padding-top: 7px;
  padding-bottom: 7px;
  background: #2355AF;
  color: #fff;
  font-weight: bold;
}
.lift-table tbody tr:nth-child(even) {
  background: #f1f1f1;
}
.lift-sort-button {
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: inherit;
  cursor: pointer;
}
.lift-sort-button:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}
.lift-sort-button .fa {
  margin-left: 0.5rem;
}
.lift-table tbody th {
  min-width: 210px;
  font-weight: normal;
}
.lift-table .lift-rank {
  min-width: 55px;
  text-align: center;
}
.lift-label {
  min-width: 145px;
}
.lift-table .lift-metric {
  text-align: right;
}
.lift-table thead .lift-metric {
  width: 8%;
}
.lift-table tbody .lift-metric {
  white-space: nowrap;
}
.lift-table thead th:nth-child(4),
.lift-table tbody td:nth-child(4),
.lift-table thead th:nth-child(5),
.lift-table tbody td:nth-child(5),
.lift-table thead th:nth-child(6),
.lift-table tbody td:nth-child(6) {
  border-left: 1px solid #aaa;
}
.lift-table tfoot {
  border-top: 1px solid #999;
}
.lift-table tfoot th {
  text-align: right;
}
.lift-total {
  font-weight: bold;
}
.lift-table-stake-total {
  color: #666;
}
.lift-growth-total {
  border-top: 1px solid #999;
}
</style>
